import React from 'react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Crown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DonateButton } from '@/components/DonateButton';
import { SEOHead } from '@/components/SEOHead';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const premiumFeatures = [
  'Full monthly calendar — all nights, all locations',
  'Moon Planner with supermoon tracking',
  'Trip Planner for multi-stop itineraries',
  'Unlimited saved locations',
  'PDF, Excel, CSV, and ICS exports',
  'Nightly visibility ratings (1–10)',
  'Historical weather data',
];

const Donate: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();

  const handleGetPremium = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          returnUrl: `${window.location.origin}/dashboard?welcome=true`,
          referralId: getRewardfulReferral(),
        },
      });

      if (error) throw error;
      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Unable to start checkout. Please try again.');
    }
  };

  return (
    <>
      <SEOHead
        title="Support Milky Way Planner — Premium & Donations"
        description="Support the free astrophotography tools at Milky Way Planner. Subscribe to premium for $25/year or make a one-time donation."
        canonicalUrl="/donate"
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Support This Project
            </h1>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-12">

          {/* Two-column: Photo + story on left, Premium on right */}
          <div className="grid md:grid-cols-2 gap-8 items-start">

            {/* Left: Photo + story */}
            <div className="flex flex-col items-center text-center">
              <OptimizedImage
                src="/images/eric-d-brown.webp"
                alt="Eric D. Brown, creator of Milky Way Planner"
                width={400}
                className="w-48 h-48 rounded-full object-cover mb-6"
              />
              <p className="text-muted-foreground leading-relaxed">
                I'm Eric, a photographer and software engineer who built Milky Way Planner because I wanted better tools for planning night sky shoots. What started as a weekend project has grown into something thousands of astrophotographers use every season. I keep the core tools free because I believe everyone should be able to plan a great shoot, regardless of budget.
              </p>
            </div>

            {/* Right: Premium card */}
            {isSubscribed ? (
              <div className="flex flex-col items-center justify-center py-8 border border-green-500/20 bg-green-500/5 rounded-lg text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-900/20">
                  <Check className="h-6 w-6 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">You're a Premium member</h2>
                <p className="text-muted-foreground text-sm px-6">
                  Thank you for supporting Milky Way Planner. You're helping keep this project alive.
                </p>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-gold to-gold-light" />
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-gold" />
                    <h2 className="text-lg font-semibold text-foreground">The best way to support us</h2>
                  </div>
                  <p className="text-muted-foreground text-sm mb-5">
                    Go premium and get the full toolkit while keeping Milky Way Planner running.
                  </p>

                  <ul className="space-y-2 mb-6">
                    {premiumFeatures.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                        <span className="text-gold mt-0.5 shrink-0">&#10022;</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="pt-5 border-t border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-2xl font-bold text-foreground tracking-tight">$25</span>
                        <span className="text-muted-foreground ml-1">/year</span>
                        <span className="text-xs text-muted-foreground ml-2">($2.08/mo)</span>
                      </div>
                    </div>
                    <Button
                      variant="premium"
                      onClick={handleGetPremium}
                      size="lg"
                      className="w-full"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Get Premium
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Donation Section */}
          <div className="relative">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="border border-border rounded-lg bg-secondary/30 p-8 text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">One-Time Donation</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
                Not ready for premium? A one-time donation helps keep the free tools running for everyone.
              </p>
              <div className="flex justify-center">
                <DonateButton />
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Donate;
